import { useState, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "@/hooks/useAlert";
import { type ConversationMessage, WingmanApiError } from "@/services/wingman";
import { mergeRecoveredHistory } from "@/services/wingman/omega-results";
import { withWorkflowSelection } from "@/services/wingman/workflowSelection";

export type WorkflowTest = { workflowId: string };
export type WorkflowDebugAction = "step" | "run" | "stop";
export type WorkflowDebug = { sessionId: string; action: WorkflowDebugAction };

/** A turn the workflow UI sends on the user's behalf, rather than composer text. */
export type WorkflowAction =
  | { test: WorkflowTest }
  | { debug: WorkflowDebug };

export type WorkflowRecovery = "ready" | "reconciling" | "blocked";

interface UseWorkflowTurnsParams {
  /** Whether this conversation has Wingman's workflow tools (Wingman v2 only). */
  available: boolean;
  /** Saved workflow that ordinary turns refer to. */
  selectedWorkflowId?: string;
  /** Runs before each turn; resolving false refuses the turn. */
  prepareWorkflowTurn?: () => Promise<boolean>;
  /** Reads the persisted conversation, or null when there is none to read. */
  listHistory: (() => Promise<ConversationMessage[]>) | null;
  setMessages: Dispatch<SetStateAction<ConversationMessage[]>>;
}

/**
 * The workflow side of a GailGPT conversation: how Test and debug
 * turns are phrased, the builder's save before each turn, and
 * recovering history after a turn that may already have saved or tested.
 * See docs/workflows/gailgpt-workflow-building.md.
 */
export function useWorkflowTurns({
  available,
  selectedWorkflowId,
  prepareWorkflowTurn,
  listHistory,
  setMessages,
}: UseWorkflowTurnsParams) {
  const { t } = useTranslation("ask");
  const { setAlert } = useAlert();
  const [error, setError] = useState<string | null>(null);
  const [recovery, setRecovery] = useState<WorkflowRecovery>("ready");

  /** The text Wingman receives for an ordinary turn. */
  const ordinaryText = (text: string): string =>
    available && selectedWorkflowId ? withWorkflowSelection(text, selectedWorkflowId) : text;

  const actionText = (action: WorkflowAction): string => {
    if ("test" in action) {
      return t("workflowTurns.test", { workflowId: action.test.workflowId });
    }
    return t(`workflowTurns.debug.${action.debug.action}`, { sessionId: action.debug.sessionId });
  };

  /** Lets the open workflow builder save before Wingman reads the workflow. */
  const prepareTurn = async (): Promise<boolean> => {
    if (!prepareWorkflowTurn || (await prepareWorkflowTurn())) return true;
    setAlert({ status: "error", message: t("workflowTurns.canvasNotSaved"), secondsToClose: 10 });
    return false;
  };

  /** Actionable text for a turn that failed after a save or test may have happened. */
  const reportFailure = (failure: unknown) => {
    const status = failure instanceof WingmanApiError ? failure.status : undefined;
    const action = t(failureActionKey(status));
    setError(
      [t("workflowTurns.failed.unfinished"), status ? t("workflowTurns.failed.status", { status }) : null, action]
        .filter(Boolean)
        .join(" ")
    );
  };

  /** Replaces local messages with persisted history, never replaying generation. */
  const reconcile = async () => {
    if (!listHistory) return;
    setRecovery("reconciling");
    try {
      const history = await listHistory();
      if (!history.length) throw new Error("Conversation history is unavailable");
      setMessages((current) => mergeRecoveredHistory(current, history));
      setRecovery("ready");
    } catch {
      setRecovery("blocked");
    }
  };

  return {
    available,
    recovery,
    error,
    clearError: () => setError(null),
    ordinaryText,
    actionText,
    prepareTurn,
    reportFailure,
    reconcile,
  };
}

function failureActionKey(status: number | null | undefined): string {
  if (status === 401) return "workflowTurns.failed.signIn";
  if (status === 403) return "workflowTurns.failed.permissions";
  if (status != null && status >= 500) return "workflowTurns.failed.service";
  return "workflowTurns.failed.review";
}
